/*
 * theme.js — client-site theme manager (System / Light / Dark).
 *
 * Preference is stored in localStorage as `jkpfactors_theme_preference`
 * ('system' | 'light' | 'dark', default 'system') — matching the existing
 * `jkpfactors_*` convention. The resolved theme ('light' | 'dark') is written
 * to <html data-theme>, which the CSS tokens in theme.css respond to.
 *
 * A small inline <head> script applies the resolved theme before first paint
 * (no FOUC); this module owns the live management: the nav toggle, OS-change
 * tracking while in System mode, and a `themechange` event for charts/Prism.
 */

const STORAGE_KEY = 'jkpfactors_theme_preference';
const VALID = ['system', 'light', 'dark'];
const darkQuery = window.matchMedia('(prefers-color-scheme: dark)');

function getPreference() {
    try {
        const v = localStorage.getItem(STORAGE_KEY);
        return VALID.includes(v) ? v : 'system';
    } catch (e) {
        return 'system';
    }
}

function resolve(preference) {
    if (preference === 'light' || preference === 'dark') return preference;
    return darkQuery.matches ? 'dark' : 'light';
}

function apply(resolved) {
    document.documentElement.setAttribute('data-theme', resolved);
}

function dispatch(resolved, preference) {
    document.dispatchEvent(new CustomEvent('themechange', {
        detail: { theme: resolved, preference },
    }));
}

/** Reflect the active preference on the 3-state toggle control. */
function syncToggleUI(preference) {
    document.querySelectorAll('[data-theme-choice]').forEach((btn) => {
        const active = btn.dataset.themeChoice === preference;
        btn.classList.toggle('active', active);
        btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
}

function setPreference(preference) {
    if (!VALID.includes(preference)) return;
    try { localStorage.setItem(STORAGE_KEY, preference); } catch (e) { /* ignore */ }
    const resolved = resolve(preference);
    apply(resolved);
    syncToggleUI(preference);
    dispatch(resolved, preference);
}

/** Public surface for other modules (e.g. charts) to read the current theme. */
window.jkpTheme = {
    current: () => document.documentElement.getAttribute('data-theme') || 'light',
    preference: getPreference,
    set: setPreference,
};

function init() {
    const preference = getPreference();
    // The inline head script already applied the resolved theme; re-assert and sync UI.
    apply(resolve(preference));
    syncToggleUI(preference);

    // Wire the nav toggle (buttons carry data-theme-choice="system|light|dark").
    document.querySelectorAll('[data-theme-choice]').forEach((btn) => {
        btn.addEventListener('click', () => setPreference(btn.dataset.themeChoice));
    });

    // Track OS changes while in System mode.
    const onOSChange = () => {
        if (getPreference() === 'system') {
            const resolved = resolve('system');
            apply(resolved);
            dispatch(resolved, 'system');
        }
    };
    if (darkQuery.addEventListener) darkQuery.addEventListener('change', onOSChange);
    else if (darkQuery.addListener) darkQuery.addListener(onOSChange); // older Safari

    // Enable transitions only after the initial paint.
    requestAnimationFrame(() => document.documentElement.classList.add('theme-ready'));
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
